import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import * as api from "../../api/index";
import { FiEdit, FiPlus, FiLogOut,FiCreditCard, FiHome, FiPlusSquare, FiMail, FiUser, FiTrash2 } from "react-icons/fi";

export default function ProductSellPage() {
  const navigate = useNavigate();
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const token = localStorage.getItem("token");
  
  let decoded = null;
  let email = null;
  let typeofuser = null;
  let userId =  null;

  if (token) {
    try {
      decoded = jwtDecode(token);
      email = decoded?.email || decoded?.Email;
      typeofuser = decoded.typeofuser;
      userId = decoded.id || decoded._id;
    } catch {
      console.warn("Invalid token");
    }
  }

  const fetchMyProducts = async (currentPage = 1) => {
    if (!email) return setError("No user email found");
    try {
      setLoading(true);
      const res = await api.fetchUserProducts(email, currentPage);
      if (res.data.success) {
        setMyProducts(res.data.products);
        setTotalPages(res.data.totalPages || 1);
        setError("");
      } else {
        setMyProducts([]);
        setError(res.data.message || "No products found");
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load your products");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      const res = await api.deleteProduct(confirmDelete);
      if (res.data.success) {
        setMyProducts(myProducts.filter((p) => p._id !== confirmDelete));
        alert("Product deleted successfully.");
      } else {
        alert(res.data.message || "Failed to delete product.");
      }
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("An error occurred while deleting the product.");
    } finally {
      setConfirmDelete(null);
    }
  };

  useEffect(() => {
    fetchMyProducts(page);
  }, [page]);

  return (
    <>
      <div className="flex flex-col min-h-screen bg-gray-50 pb-24">
        {/* 🔝 Sticky Top Bar */}
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center shadow-sm">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition flex items-center gap-1 text-gray-700"
          >
            <IoMdArrowBack size={24} />
            <span className="hidden sm:inline font-bold">Back</span>
          </button>
          
          <h1 className="text-lg font-black text-gray-900 tracking-tight">Manage Inventory</h1>

          <button
            onClick={() => navigate("/sell-product")}
            className="bg-orange-600 text-white px-5 py-2 rounded-xl font-bold text-sm shadow-lg shadow-orange-100 flex items-center gap-2 hover:bg-orange-700 transition-all active:scale-95"
          >
            <FiPlus /> Sell
          </button>
        </div>

        <div className="max-w-6xl mx-auto w-full p-4 sm:p-6">
          {/* 🏷 Header Info */}
          <div className="mb-8">
            <h1 className="text-2xl font-black text-gray-900">My Products</h1>
            <p className="text-gray-500 text-sm font-medium">You have listed {myProducts.length} items for sale</p>
          </div>

          {/* 🕒 Loading & Error */}
          {loading && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-64 bg-gray-200 animate-pulse rounded-[2rem]" />
              ))}
            </div>
          )}
          {!loading && error && (
            <div className="text-center py-10">
               <p className="text-red-500 font-bold">{error}</p>
            </div>
          )}

          {/* 🧱 Modern Product Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {!loading && myProducts.length > 0 ? (
              myProducts.map((product) => (
                <div
                  key={product._id}
                  className="group bg-white rounded-[2rem] border border-gray-100 hover:border-orange-500 transition-all duration-300 flex flex-col overflow-hidden shadow-sm hover:shadow-xl relative"
                >
                  {/* Delete Button */}
                  <button
                    onClick={() => setConfirmDelete(product._id)}
                    className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur text-red-500 rounded-full hover:bg-red-500 hover:text-white transition-all shadow-md"
                  >
                    <FiTrash2 size={16} />
                  </button>

                  <div className="relative h-32 sm:h-48 overflow-hidden">
                    <img
                      src={product.images[0] || "/placeholder.jpg"}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                    />
                  </div>

                  <div className="p-3 sm:p-5 flex flex-col flex-1">
                    <h2 className="font-black text-gray-900 text-sm sm:text-lg truncate group-hover:text-orange-600 transition">
                      {product.name}
                    </h2>
                    
                    <p className="text-orange-600 font-black text-sm sm:text-lg mt-1">
                      ₦{product.price.toLocaleString()}
                    </p>

                    <p className="text-gray-400 text-[10px] font-bold uppercase tracking-tight mt-1 truncate">
                      {product?.product} • {product?.model}
                    </p>

                    <div className="mt-4 pt-4 border-t border-gray-50 flex gap-2">
                      <button
                        onClick={() => navigate(`/view-product/${product._id}`)}
                        className="flex-1 bg-gray-900 text-white py-2.5 rounded-2xl hover:bg-orange-600 transition shadow-lg text-[11px] sm:text-sm font-bold"
                      >
                        View
                      </button>
                      <button
                        onClick={() => navigate(`/edit-product/${product._id}`)}
                        className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-2xl hover:bg-gray-200 transition text-[11px] sm:text-sm font-bold flex items-center justify-center gap-1"
                      >
                        <FiEdit size={14}/> Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* --- FIX: BIG PLUS SIGN IN THE MIDDLE --- */
              !loading && (
                <div className="col-span-full flex flex-col items-center justify-center py-24 px-4 bg-white rounded-[3rem] border-2 border-dashed border-gray-200 transition-all">
                  <button
                    onClick={() => navigate("/sell-product")}
                    className="group relative flex items-center justify-center w-28 h-28 sm:w-40 sm:h-40 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-all duration-300 shadow-2xl shadow-orange-200 active:scale-90"
                  >
                    <FiPlus size={64} className="group-hover:rotate-90 transition-transform duration-300" />
                    {/* Pulsing visual indicator */}
                    <span className="absolute inset-0 rounded-full bg-orange-600 animate-ping opacity-20"></span>
                  </button>

                  <div className="text-center mt-10">
                    <h3 className="text-2xl font-black text-gray-900">Start Selling</h3>
                    <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">
                      You haven't listed any products yet. Tap the button to post your first item!
                    </p>
                  </div>
                </div>
              )
            )}
          </div>

          {/* 📄 Modern Pagination */}
          {!loading && myProducts.length > 0 && (
            <div className="flex justify-center items-center mt-12 mb-10 gap-6">
              <button
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className={`px-6 py-2 rounded-xl font-bold transition-all ${
                  page <= 1
                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                    : "bg-white text-gray-700 shadow-md hover:bg-gray-50 active:scale-95"
                }`}
              >
                Prev
              </button>

              <span className="text-gray-900 font-black text-sm uppercase tracking-widest">
                {page} / {totalPages}
              </span>

              <button
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                className={`px-6 py-2 rounded-xl font-bold transition-all ${
                  page >= totalPages
                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                    : "bg-white text-gray-700 shadow-md hover:bg-gray-50 active:scale-95"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* 🧭 Bottom Navigation Bar (Fixed for Mobile) */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-gray-100 md:flex justify-around items-center py-3 z-50 hidden shadow-[0_-5px_15px_rgba(0,0,0,0.05)]">
          <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
            <FiHome size={22} />
            <span className="text-[10px] font-bold">Home</span>
          </button>
          <button onClick={() => navigate("/sellproduct")} className="flex flex-col items-center gap-1 text-orange-600 transition-colors">
            <FiPlusSquare size={22} />
            <span className="text-[10px] font-bold">Post</span>
          </button>
               <button onClick={() => navigate(`/balancepage/${userId}`)} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                    <FiCreditCard size={22} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Wallet</span>
                  </button>
          <button onClick={() => navigate("/conversations")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
            <FiMail size={22} />
            <span className="text-[10px] font-bold">Inbox</span>
          </button>
          <button onClick={() => typeofuser === "Customer" ? navigate("/profile") : navigate("/profile-seller")} className="flex flex-col items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
            <FiUser size={22} />
            <span className="text-[10px] font-bold">Profile</span>
          </button>
        </nav>
      </div>

      {/* 🗑 Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[100] p-4">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl w-full max-w-sm text-center transform transition-all border border-gray-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
               <FiTrash2 size={30} />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">Delete Product?</h3>
            <p className="mb-8 text-gray-500 font-medium">
              This action cannot be undone. Are you sure you want to remove this listing?
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDeleteConfirm}
                className="w-full py-4 bg-red-500 text-white rounded-2xl font-bold hover:bg-red-600 transition-all shadow-lg shadow-red-100"
              >
                Yes, Delete Listing
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold hover:bg-gray-200 transition-all"
              >
                Keep Product
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}